// @flow
import React, {useContext, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import GuiUtils from "../SUPPORT/GuiUtils";
import {StoreContext} from "../SUPPORT/store";
import LIUtils from "../SUPPORT/LIUtils";
import LIButton from "./LIButton";
import Popup from "./Popup";
import CheckInput from "./CheckInput";
import MagBadge from "./MagBadge";
import GeneralSlidePlaceArrow from "./GeneralSlidePlaceArrow";

const GeneralMagView = ({magTypeInfo, magInfo, samples, handleDiscardMag, handleDiscardMagAborted, discardIsBusy,
                            allowDiscard, showPlaceLocation, onSampleClicked, showDetailsOnly=false, highlightDiscardDate=false}) => {
    
    const [globalState] = useContext(StoreContext); // for profiles
    
    const MARGINX_LEFT = 40;
    const MARGINY_TOP = 30;
    const MARGINX_RIGHT = 10;
    const MARGINY_BOTTOM = 10;
    
    const INTER_SAMPLE_MARGIN = 3;
    const MIN_INTER_SAMPLE_SPACING = 40;
    const MAX_INTER_SAMPLE_SPACING = 50;
    
    const MAX_WIDTH_PELS=2000;
    const MAX_HEIGHT_PELS=2000;
    
    const [drawContext:CanvasRenderingContext2D, setDrawContext] = useState();
    const [forcedWidth, setForcedWidth] = useState();
    const [forcedHeight, setForcedHeight] = useState();
    const [interXSpacing, setInterXSpacing] = useState(MIN_INTER_SAMPLE_SPACING);
    const [interYSpacing, setInterYSpacing] = useState(MIN_INTER_SAMPLE_SPACING);
    const [numColumns,setNumColumns] = useState(10);
    const [numRows,setNumRows] = useState(10);
    const [hoveredSample, setHoveredSample] = useState();
    const [hoveredMouseCoords, setHoveredMouseCoords] = useState([0,0]);
    const [haveOverrideCBValue, setHaveOverrideCBValue] = useState(false);
    
    const canvasRef = useRef(null);

    // const testSamples = [
    //     {id: "1", sampleType:3, magSection: 0, positionAbsolute: 1, barcode:"20S-1234-1-A-1",
    //         sampleNumber:"1-A-1", caseNumber:"20S-1234", hasCaseAndSampleInfo: true,
    //         dateAddedToMag: "2021-04-19T17:12:00",
    //         profileId:1}, // all info
    //     {id: "2", sampleType:3, magSection: 0, positionAbsolute: 2, barcode:"20S-1234-1-A-2",
    //         sampleNumber:"1-A-2", caseNumber:"20S-1234", hasCaseAndSampleInfo: true,
    //         dateAddedToMag: "2021-04-19T18:12:00",
    //         profileId:2}, // all info
    //     {id: "3", sampleType:3, magSection: 2, positionAbsolute: 4, barcode:"22S-1234-1-A-2",
    //         sampleNumber:"", caseNumber:"20S-1234", hasCaseAndSampleInfo: false,
    //         dateAddedToMag: "2021-04-19T19:12:00",
    //         profileId:1}, // case only
    //     {id: "4", sampleType:3, magSection: 4, positionAbsolute: 2, barcode:"22S-1234-1-A-2",
    //         sampleNumber:"", caseNumber:"", hasCaseAndSampleInfo: false,
    //         dateAddedToMag: "2021-04-19T20:12:00",
    //         profileId:4}, // missing info with profile
    //     {id: "5", sampleType:3, magSection: 4, positionAbsolute: 3, barcode:"22S-1234-1-A-2",
    //         sampleNumber:"", caseNumber:"", hasCaseAndSampleInfo: false,
    //         dateAddedToMag: "2021-04-19T21:12:00",
    //         profileId:0}, // no profile
    //     {id: "6", sampleType:3, magSection: 2, positionAbsolute: 3, barcode:"22S-1234-1-A-2",
    //         sampleNumber:"1-A-1", caseNumber:"20S-1234", hasCaseAndSampleInfo: true, isDuplicate: true,
    //         dateAddedToMag: "2021-04-19T22:12:00",
    //         profileId:1}, // no profile
    //     {id: "7", sampleType:3, magSection: 4, positionAbsolute: 5, barcode:"22S-1234-1-A-10",
    //         sampleNumber:"", caseNumber:"", hasCaseAndSampleInfo: false,
    //         dateAddedToMag: "2021-04-19T22:12:00",
    //         profileId:8}, // unmapped with profile (caseMappedLater)
    // ];
    // samples = testSamples;
    
    useEffect( () => {
        if(magTypeInfo) {
            setNumColumns(prev => magTypeInfo.numColumns);
            setNumRows(prev => magTypeInfo.numRows);
            configureWidthAndHeight(magTypeInfo.numColumns, magTypeInfo.numRows);
        }
        else { // default (nothing to show) sizing
            setNumColumns(prev => 0);
            setNumRows(prev => 0);
            configureWidthAndHeight(6,6);
        }
    }, [magTypeInfo]);
    
    useEffect( () => {
        if (!showDetailsOnly) {
            drawMagazine();
        }
    },[samples,magTypeInfo,forcedHeight,forcedWidth,hoveredSample]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        if(hoveredSample) {
            showSamplePopup(hoveredSample);
        }
        else {
            showSamplePopup(null);
        }
    },[hoveredSample]); // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        setHaveOverrideCBValue(false);
    },[magInfo]);
    
    function configureWidthAndHeight(numColumns, numRows) {
        let interXSpacing = (MAX_WIDTH_PELS-(MARGINX_LEFT+MARGINX_RIGHT)) / numColumns;
        if(interXSpacing < MIN_INTER_SAMPLE_SPACING) { // need a minimum inter sample spacing
            interXSpacing = MIN_INTER_SAMPLE_SPACING;
        }
        if(interXSpacing > MAX_INTER_SAMPLE_SPACING) { // too much space looks silly
            interXSpacing = MAX_INTER_SAMPLE_SPACING;
        }
        let interYSpacing = (MAX_HEIGHT_PELS-(MARGINY_TOP+MARGINY_BOTTOM)) / numRows;
        if(interYSpacing < MIN_INTER_SAMPLE_SPACING) {
            interYSpacing = MIN_INTER_SAMPLE_SPACING;
        }
        if(interYSpacing > MAX_INTER_SAMPLE_SPACING) {
            interYSpacing = MAX_INTER_SAMPLE_SPACING;
        }
        setInterXSpacing(prev => interXSpacing);
        setInterYSpacing(prev => interYSpacing);

        const requiredWidth = interXSpacing * numColumns + MARGINX_LEFT + MARGINX_RIGHT;
        setForcedWidth(prev => requiredWidth);

        const requiredHeight = interYSpacing * numRows + MARGINY_TOP + MARGINY_BOTTOM;
        setForcedHeight(prev => requiredHeight);
    }
    
    function showSamplePopup(sampleItem) {
        
        const magnifier = document.getElementById("samplePopup");
        if(sampleItem===null) { // pop down
            magnifier.classList.remove("show");
            magnifier.classList.add("hide");
            return;
        }
        
        magnifier.style.left=(hoveredMouseCoords[0]-100+canvasRef.current.offsetLeft)+"px";
        magnifier.style.top=(hoveredMouseCoords[1]-50+canvasRef.current.offsetTop)+"px";

        const samplePopupText = document.getElementById("samplePopupText");
        // show the case and sample, otherwise the barcode
        samplePopupText.innerText = GuiUtils.BuildSampleDetailsStr(
            sampleItem.barcode,sampleItem.caseNumber,sampleItem.sampleNumber);

        // show it
        magnifier?.classList.remove("hide");
        magnifier?.classList.add("show");
    }
    
    function drawMagazine() {
        const sampleRadiusPels = Math.min(interXSpacing,interYSpacing) / 2 - INTER_SAMPLE_MARGIN;

        // this is a strange way to do this, but the drawContext is somehow not always available
        // after a reload.
        let useThisDrawContext = drawContext;
        if(!useThisDrawContext) { // attempt for force (on reload)
            useThisDrawContext = canvasRef.current.getContext('2d');
            setDrawContext(useThisDrawContext);
        } 
        
        useThisDrawContext.clearRect(0,0,forcedWidth,forcedHeight);
        if(!numColumns || numColumns===0 || !numRows || numRows===0) {
            drawNoMagazine(useThisDrawContext, forcedWidth, forcedHeight);
            return;
        }
        // rows start at 1, columns start at 0 (I know... - archiver legacy with positionAbsolute vs positionOrder)
        for(let col=0; col<numColumns; col++) {
            let x = MARGINX_LEFT + interXSpacing/2 + col * interXSpacing;
            drawColumnHeader(useThisDrawContext,x, MARGINY_TOP,col+1, magTypeInfo?.columnNamesAreAlpha??false);
            for (let row = 1; row <= numRows; row++) {
                let y = MARGINY_TOP +  interYSpacing/2 + (row-1) * interYSpacing;
                if(col===0) drawRowHeader(useThisDrawContext,MARGINX_LEFT/2, y, row, magTypeInfo?.rowNamesAreAlpha??false);
                const fillColor= getSampleColorForSampleAtPosition(row,col);
                const isSelected = hoveredSample && LIUtils.isSampleAtGenMagRowAndColumn(magTypeInfo.isIndexed,hoveredSample,row,col);
                drawSingleSample(useThisDrawContext,x,y,sampleRadiusPels,fillColor,"black",isSelected);
            }
        }
    }
    
    function handleSampleClicked(e) {
        if(hoveredSample && onSampleClicked) {
            onSampleClicked(hoveredSample);
        }
    }

    function dumpSamplePopupBase() {

        return(
            <div id="samplePopup" className="li-sample-hover-popup">
                <div id="samplePopupText" className="text-center w-100"/>
            </div>
        );
    }

    //#region MOUSE_HANDLING
    
    // stolen from: https://www.chestysoft.com/imagefile/javascript/get-coordinates.asp
    function FindPosition(oElement) {
        if (typeof (oElement.offsetParent) != "undefined") {
            let posX,posY;
            for (posX = 0, posY = 0; oElement; oElement = oElement.offsetParent) {
                posX += oElement.offsetLeft;
                posY += oElement.offsetTop;
            }
            return [posX, posY];
        } else {
            return [oElement.x, oElement.y];
        }
    }
    function handleMouseMovedOverMag(e) {
        const parentPos = FindPosition(canvasRef.current);
        const mouseCoords = [e.pageX-parentPos[0], e.pageY-parentPos[1]];
        selectSampleUnderMouse(mouseCoords[0],mouseCoords[1]);
    }
    function handleTouchOverMag(e) {
        const parentPos = FindPosition(canvasRef.current);
        const mouseCoords=[e.touches[0].pageX-parentPos[0], e.touches[0].pageY-parentPos[1]];
        selectSampleUnderMouse(mouseCoords[0],mouseCoords[1]);
    }
    function FindSampleUnderMouse(x,y) {
        const col = Math.floor((x - MARGINX_LEFT) / interXSpacing);
        const row = Math.floor((y - MARGINY_TOP) / interYSpacing);
        
        if(col<0 || col>=numColumns || row<0 || row>=numRows) {
            return null;
        }
        return findSampleAtPosition(samples,row+1,col);
    }
    
    function selectSampleUnderMouse(x,y) {
        const sampleUnderMouse = FindSampleUnderMouse(x,y);
        if(sampleUnderMouse) {
            if(hoveredSample && hoveredSample.id === sampleUnderMouse.id) {
                return;
            }
            setHoveredSample(sampleUnderMouse);
            setHoveredMouseCoords([x,y]);
        }
        else {
            setHoveredSample(null);
        }
    }
    function handleMouseOut() {
        setHoveredSample(null);
    }

    //#endregion MOUSE_HANDLING
    
    //#region HELPERS
    
    function drawSingleSample(drawContext, x,y,radius,fillColor="blue", strokeColor="black", isSelected=false, isSquare=false) {
        
        drawContext.fillStyle = fillColor;
        drawContext.beginPath();
        if(isSquare) {
            drawContext.rect(x - radius, y - radius, 2 * radius, 2 * radius);
        }
        else {
            drawContext.arc(x,y,radius,0,2*Math.PI);
        }
        drawContext.fill();
        
        // outer stroke
        if(isSelected) {
            drawContext.strokeStyle = "yellow";
            drawContext.lineWidth=7;
        }
        else {
            drawContext.strokeStyle = strokeColor;
            drawContext.lineWidth = 3;
        }
        drawContext.stroke();
    }
    
    function drawColumnHeader(drawContext,x, y, col, areAlpha:boolean=false) {
        drawContext.textAlign = "center";
        drawContext.font = "20px Arial";

        const theText = areAlpha ? GuiUtils.numberToAlpha(col) : col;
        
        const textHeight = GuiUtils.GetRenderedTextHeight(drawContext, theText);
        drawContext.fillStyle = "black";
        drawContext.fillText(theText, x, y-textHeight/4);
    }
    
    function drawNoMagazine(drawContext, forcedWidth, forcedHeight) {
        drawContext.textAlign = "center";
        drawContext.font = "20px Arial";
        drawContext.fillStyle = "black";
        drawContext.fillText("Scan a Rack", forcedWidth/2, forcedHeight/2);
    }
    
    function drawRowHeader(drawContext, x, y, row, areAlpha:boolean=false) {
        drawContext.textAlign = "center";
        drawContext.font = "20px Arial";
        
        const theText = areAlpha ? GuiUtils.numberToAlpha(row) : row;
        
        const textHeight = GuiUtils.GetRenderedTextHeight(drawContext, theText);
        const textWidth = GuiUtils.GetRenderedTextWidth(drawContext, theText);
        drawContext.fillStyle = "black";
        drawContext.fillText(theText, x+textWidth/5, y + textHeight/4);
    }

    function findSampleAtPosition(samples, row, col) {
        if (!magTypeInfo) return false;
        return samples.find(s => {
           return LIUtils.isSampleAtGenMagRowAndColumn(magTypeInfo?.isIndexed, s,row,col);
        });
    }
    
    function getSampleColorForSampleAtPosition(row, col) {
        const sample = findSampleAtPosition(samples, row, col);
        if(sample) {
            
            // check these first
            const profileId = sample.profileId;
            if(!profileId || profileId===0) return "purple";
            if(sample.isDuplicate) return "orange";

            let detailsMappedLater = false;
            if(globalState.profiles?.initialized && globalState.profiles?.items) {
                const profileInfo = globalState.profiles.items.find(p => p.id === profileId);
                if (!profileInfo) { // we don't have a profile for this sample loaded ... is it in the wrong mag!?
                    return "yellow";
                } else if (profileInfo.holdsSampleType !== magTypeInfo.holdsSampleType) { // wrong type of sample in this mag
                    return "yellow";
                } else if (profileInfo.destinationMagProfileId > 0 && profileInfo.destinationMagProfileId !== magInfo.parentProfileId) {
                    // wrong actual mag profile for this sample (it's supposed to go into a specific mag profile, and it's not this one)
                    return "yellow";
                } else {
                    if (profileInfo.isCaseMappedExternal || profileInfo.isSampleNumMappedLater) {
                        detailsMappedLater = true;
                    }
                }
            }
            // check this after checking isMappedExternal or sampleMappedLater
            if(!LIUtils.SampleHasCriticalInfo(sample.caseNumber, sample.sampleNumber, sample.profileId)) {
                if(detailsMappedLater) {  // mapper may not have run yet
                    return "gray";
                }
                // definitely missing info - in NA
                return "red";
            }
            if (sample.isDuplicate) {
                return "orange";
            }
            
            // all good - sample is there and good
            return "blue";
        }

        // no sample found
        return "white";
    }
    
    function getOldestAndNewestDatesFromSamples(samples): {oldestDate:Date, newestDate:Date} {

        // sort samples by dateAddedToMag and store the oldest and newest
        let oldestDate = null;
        let newestDate = null;

        if(!samples || samples.length===0) {
            return {oldestDate, newestDate};
        }
        
        samples.forEach(s => {
            const dateAdded =  LIUtils.dateFromJsonUtcDateStr(s.dateAddedToMag);
            if(!oldestDate || dateAdded < oldestDate) {
                oldestDate = dateAdded;
            }
            if(!newestDate || dateAdded > newestDate) {
                newestDate = dateAdded;
            }
        });
        return {oldestDate, newestDate};
    }

    function drawNextPlaceLocation(showPlaceLocation:{row:number, column:number}, isSquare:boolean=false) {

        const SLIDE_ARROW_HEIGHT = 75;
        
        if (!showPlaceLocation) return null;

        // rows start at 1, columns start at 0 (I know... - archiver legacy with positionAbsolute vs positionOrder)
        let x = MARGINX_LEFT + interXSpacing/2 + showPlaceLocation.column * interXSpacing;
        let y = MARGINY_TOP + interYSpacing/2 + (showPlaceLocation.row-1) * interYSpacing - SLIDE_ARROW_HEIGHT/2;

        // const a = arrowRef.current;
        // if(!a) return null;
        const leftOffset=canvasRef.current.offsetLeft+x;
        const topOffset=canvasRef.current.offsetTop+y;
        // //a.style.display= (show)?"block":"none";
        //
        // let src=arrowLeft;
        // let alt="arrow pointing left";
        // return(<img id="slideArrow" ref={arrowRef} src={src} className="bounce" alt={alt} style={{top:topOffset,left:leftOffset}}/>);
        return (
            <GeneralSlidePlaceArrow x={leftOffset} y={topOffset} timed={false} flip={true}/>
        );
    }
    
    //#endregion HELPERS
    
    //#region DUMPING
    function dumpMagDetails() {
        const {oldestDate, newestDate} = getOldestAndNewestDatesFromSamples(samples);
        // noinspection PointlessArithmeticExpressionJS
        const totalSpots = magTypeInfo===null?0:(numRows*numColumns);
        const percentPop = totalSpots===0?0: Math.round((samples.length / totalSpots) * 100);
       
        let dateForDisposal="-";
        let dateClassName="";
        let disposalLabel = "Disposal Date";
        let disposalWarning="";
        if(doesHaveDumpDate(magInfo)) {
            const canDiscard = discardDateHasPassed(magInfo);
            dateForDisposal = LIUtils.shortDateTimeFromJsonUtcDateStr(magInfo.canBeDumpedDate);
            if (!canDiscard) { // can't discard yet
                disposalLabel = "Can be disposed on:";
                disposalWarning = <><br/><span className="badge li-bg-danger li-fg-white">Not Ready For Disposal</span></>
            } else {
                disposalLabel = "Ready for disposal:";
            }
            if (highlightDiscardDate) {
                if (!canDiscard) { // can't discard yet
                    dateClassName = "li-fg-danger font-weight-bold";
                } else {
                    dateClassName = "li-fg-success font-weight-bold";
                }
            }
        }
        
        const oldestDateStr = LIUtils.dateToShortDateTimeString(oldestDate);
        const newestDateStr = LIUtils.dateToShortDateTimeString(newestDate);
        const percentPopInfo = totalSpots!==0?`${percentPop}% (${samples.length}/${totalSpots})`:"-";
        return(
            <div>
                <h2 className="text-center">Magazine Details</h2>
                <table className="table table-sm mt-5">
                    <tbody>
                        <tr><td className="font-weight-bold">Barcode:</td><td><MagBadge text={magInfo?.barcode??"-"} small/></td></tr>
                        <tr><td className="font-weight-bold">Magazine Type:</td><td>{magTypeInfo?.description??"-"}</td></tr>
                        <tr><td className="font-weight-bold">Populated (%):</td><td>{percentPopInfo}</td></tr>
                        <tr><td className="font-weight-bold">Date Oldest:</td><td>{oldestDateStr===""?"-":oldestDateStr}</td></tr>
                        <tr><td className="font-weight-bold">Date Newest:</td><td>{newestDateStr===""?"-":newestDateStr}</td></tr>
                        <tr><td className={"font-weight-bold "+dateClassName}>{disposalLabel}</td><td className={dateClassName}>{dateForDisposal}{disposalWarning}</td></tr>
                    </tbody>
                </table>
            </div>
        );
    }
    
    function doesHaveDumpDate(magInfo):boolean {
        return magInfo?.canBeDumpedDate && magInfo.canBeDumpedDate!==0;
    }

    function handleForceOverrideCBChanged(e) {
        setHaveOverrideCBValue(e.target.checked);
    }

    function dumpDiscardButtonIfRequired() {
        if(!allowDiscard) return null;
        if(!doesHaveDumpDate(magInfo) || handleDiscardMag===undefined) return null;
        
        let checkBox=null;
        if(doesHaveDumpDate(magInfo) && !discardDateHasPassed(magInfo)) { // show the override checkbox
            checkBox = <CheckInput label="Enable Force Discard" name="forceDiscardCB" showCheckLeft
                             value={haveOverrideCBValue.toString()} checked={haveOverrideCBValue} onChanged={(e) => handleForceOverrideCBChanged(e)}/>;
        }
        
        return (
            <div className="text-center my-5">
                {checkBox}
                <div className="mb-5">
                    <LIButton label="Discard Magazine" primary onClick={()=>handleDiscardMagazine(magInfo.barcode)} 
                              className="btn btn-danger" showBusy={discardIsBusy} disabled={discardIsBusy||(checkBox!==null && haveOverrideCBValue===false)}/>
                </div>
                
            </div>
        );
    }
    
    function discardDateHasPassed(magInfo):boolean {
        if(!magInfo) return false;
        
        const canBeDumpedDate = LIUtils.dateFromJsonUtcDateStr(magInfo.canBeDumpedDate);
        const isPast=canBeDumpedDate < new Date();
        return canBeDumpedDate && isPast;
    }
    
    function handleDiscardMagazine(magBC:string) {
        Popup.showConfirm("Discard Mag",
            `<p>Are you sure you want to discard all samples from mag '${magBC}'?</p>` +
            "<p>This will:<ul><li>clear the magazine of all samples</li>"+
            "<li>mark all samples in this mag as <span class=\"li-fg-danger\">permanently removed</span></li></ul></p>",
            "Discard Magazine", doActualDiscardAbort, doActualDiscardMagazine);
    }
    
    function doActualDiscardAbort() {
        if(handleDiscardMagAborted) {
            handleDiscardMagAborted(); // parent call here
        }
    }
    
    function doActualDiscardMagazine() {
        if(handleDiscardMag) {
            handleDiscardMag(magInfo.barcode, haveOverrideCBValue); // parent call here
        }
    }
    //#endregion DUMPING
    
    return (
        <div className="row justify-content-around">
            <div>
                {dumpMagDetails()}
                {dumpDiscardButtonIfRequired()}
            </div>
            {!showDetailsOnly &&
                <div className="mb-5">
                    <canvas ref={canvasRef} id="magazineCanvas" width={forcedWidth} height={forcedHeight} 
                            style={{border:"2px solid black",borderRadius:"10px",backgroundColor:"var(--li-bg-light)"}}
                            onMouseMove={handleMouseMovedOverMag}
                            onMouseOut={handleMouseOut}
                            onTouchStart={handleTouchOverMag}
                            onTouchEnd={handleMouseOut}
                            onClick={handleSampleClicked}
                    />
                </div>
            }
            {dumpSamplePopupBase()}
            {showPlaceLocation && drawNextPlaceLocation(showPlaceLocation)}
        </div>
    );

}

GeneralMagView.propTypes = {
    magTypeInfo: PropTypes.object,
    samples: PropTypes.array.isRequired,     
    magInfo: PropTypes.object.isRequired,
    handleDiscardMag: PropTypes.func,
    handleDiscardMagAborted: PropTypes.func,
    discardIsBusy: PropTypes.bool,
    allowDiscard: PropTypes.bool,
    showPlaceLocation: PropTypes.object,
    onSampleClicked: PropTypes.func,
    showDetailsOnly: PropTypes.bool,
    highlightDiscardDate: PropTypes.bool
}

export default GeneralMagView;